import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { api } from "@/lib/api";
import { Radio } from "lucide-react";

/**
 * ObsHlsPlayer — plays a Cloudflare Stream Live broadcast in a channel page.
 *
 * Uses hls.js with aggressive low-latency tuning against Cloudflare's LL-HLS
 * manifests (partial segments via `?llhlsHBs=0.5`). Targets ~5-10s
 * glass-to-glass latency instead of the ~30-45s default hls.js buffer.
 *
 * Polls /api/obs/status/{username} every 2s to detect live→offline transitions.
 * Client-side 30s grace period mirrors the server-side grace so brief network
 * hiccups don't blank out the player.
 */
export default function ObsHlsPlayer({ username, className = "" }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [status, setStatus] = useState({ is_live: false, hls_url: null });
  const [graced, setGraced] = useState({ is_live: false, hls_url: null });
  const lastLiveAtRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get(`/obs/status/${username}`);
        if (cancelled) return;
        setStatus(r.data);
        if (r.data?.is_live && r.data?.hls_url) {
          lastLiveAtRef.current = Date.now();
          setGraced({ is_live: true, hls_url: r.data.hls_url });
        } else {
          const sinceLive = Date.now() - lastLiveAtRef.current;
          if (sinceLive > 30000) setGraced({ is_live: false, hls_url: null });
        }
      } catch { /* noop */ }
    };
    poll();
    const t = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(t); };
  }, [username]);

  const effectiveHlsUrl = graced.is_live ? graced.hls_url : status.hls_url;
  const effectiveIsLive = graced.is_live || status.is_live;

  useEffect(() => {
    if (!effectiveIsLive || !effectiveHlsUrl || !videoRef.current) {
      if (hlsRef.current) { try { hlsRef.current.destroy(); } catch { /* noop */ } hlsRef.current = null; }
      return undefined;
    }
    const v = videoRef.current;

    // Safari — native HLS. Just set src and let it fly.
    if (v.canPlayType("application/vnd.apple.mpegurl")) {
      v.src = effectiveHlsUrl;
      v.play().catch(() => {});
      const onLoaded = () => {
        try {
          if (v.seekable && v.seekable.length > 0) {
            v.currentTime = Math.max(0, v.seekable.end(0) - 3);
          }
        } catch { /* noop */ }
      };
      v.addEventListener("loadedmetadata", onLoaded, { once: true });
      return () => v.removeEventListener("loadedmetadata", onLoaded);
    }

    if (!Hls.isSupported()) return undefined;

    // hls.js — tune for lowest latency Cloudflare LL-HLS can provide.
    // Cloudflare emits partial segments (llhlsHBs=0.5 = 0.5s parts). With
    // these settings hls.js targets ~5-8s glass-to-glass instead of ~30s.
    const hls = new Hls({
      lowLatencyMode: true,
      backBufferLength: 4,             // seconds behind live to keep
      maxBufferLength: 8,              // don't buffer far ahead
      maxMaxBufferLength: 12,
      liveSyncDuration: 3,             // target 3s behind the live edge
      liveMaxLatencyDuration: 8,       // if we drift past 8s, jump forward
      liveDurationInfinity: true,
      maxLiveSyncPlaybackRate: 1.5,    // speed up to 1.5x to catch up
      // Faster startup — start at live edge, minimize prebuffer.
      startPosition: -1,
      startLevel: -1,
      // Faster ABR reaction on live streams
      abrEwmaFastLive: 3.0,
      abrEwmaSlowLive: 9.0,
      // Faster segment/manifest polling
      manifestLoadingTimeOut: 8000,
      manifestLoadingMaxRetry: 4,
      levelLoadingTimeOut: 8000,
      fragLoadingTimeOut: 15000,
    });

    hls.loadSource(effectiveHlsUrl);
    hls.attachMedia(v);
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      v.play().catch(() => {});
    });
    // Auto-recover from network / media errors — critical for streamers on
    // shaky uploads (their brief disconnects would otherwise kill playback).
    hls.on(Hls.Events.ERROR, (_evt, data) => {
      if (!data?.fatal) return;
      try {
        if (data.type === "networkError") hls.startLoad();
        else if (data.type === "mediaError") hls.recoverMediaError();
      } catch { /* noop */ }
    });
    hlsRef.current = hls;
    return () => { try { hls.destroy(); } catch { /* noop */ } };
  }, [effectiveIsLive, effectiveHlsUrl]);

  if (!effectiveIsLive) return null;

  const isReconnecting = graced.is_live && !status.is_live;

  return (
    <div className={`relative ${className}`} data-testid="obs-hls-player">
      <video
        ref={videoRef}
        controls
        playsInline
        autoPlay
        muted
        className="w-full h-full object-cover bg-black"
      />
      <div className="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 rounded-sm bg-[color:var(--sc-pink)]/90 text-white text-[10px] sc-mono uppercase tracking-widest pointer-events-none">
        <span className="live-dot" /> LIVE · OBS
      </div>
      <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-sm bg-black/60 text-[color:var(--sc-cyan)] text-[10px] sc-mono uppercase tracking-widest pointer-events-none">
        <Radio className="w-3 h-3" /> {isReconnecting ? "Reconnecting" : "Cloudflare"}
      </div>
    </div>
  );
}
